import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';
import { initializeSchemaFields } from 'utils/schemaUtils';
import { newCalculationStarted } from './historySlice';
import {
  xFieldSelector,
  yFieldSelector,
  zFieldSelector
} from './keyFieldsSlice';

const KNOW_LOCATION_FORMATS = [
  'esri-json',
  'geo-json',
  'geo-rss-format',
  'parquet',
  'shapefile'
];

const HUMAN_REDABLE_FORMAT = {
  'geo-json': 'GeoJSON',
  'esri-json': 'EsriJSON',
  xml: 'XML',
  delimited: 'Delimited',
  json: 'JSON',
  'geo-rss-format': 'GeoRSS',
  'rss-format': 'RSS',
  Unknown: 'Unknown'
};

interface SchemaState {
  inputSample: string;
  derivedSamples: Sample[];
  schemaFields: SchemaFields;
  formatName: FormatName | undefined;
  formatProps: FormatProps;
  hasDerivedGeometry: boolean;
}

const initialState: SchemaState = {
  inputSample: '',
  derivedSamples: [],
  schemaFields: {},
  formatName: undefined,
  formatProps: {},
  hasDerivedGeometry: false
};

const resetSchema = (state: SchemaState) => {
  state.inputSample = '';
  state.derivedSamples = [];
  state.schemaFields = {};
  state.formatName = undefined;
  state.formatProps = {};
};

export const counterSlice = createSlice({
  name: 'schema',
  initialState,
  reducers: {
    sampleChanged: (state, action: PayloadAction<string>) => {
      state.inputSample = action.payload;
    },
    formatChanged: (state, action: PayloadAction<FormatName>) => {
      state.formatName = action.payload;
      state.formatProps = {};
    },
    formatPropChanged: (
      state,
      action: PayloadAction<{ [key: string]: string | boolean }>
    ) => {
      if (
        Object.keys(action.payload).includes('flatten') &&
        action.payload.flatten === false
      ) {
        delete state.formatProps.fieldFlatteningExemptions;
      }
      state.formatProps = {
        ...state.formatProps,
        ...action.payload
      };
    },
    schemaReceived: (state, action: PayloadAction<SchemaResponse>) => {
      state.derivedSamples = action.payload.samples;
      state.schemaFields = initializeSchemaFields(action.payload);
      state.formatName = action.payload.formatName;
      state.formatProps = Object.entries(action.payload.properties).reduce(
        (acc, [key, value]) => {
          const propKey = key.split('.')[1];
          if (propKey) acc[propKey] = value;
          return acc;
        },
        {} as Record<string, string>
      );
      const hasGeometryType = !!action.payload?.schema?.geometry?.geometryType;
      const hasWkid =
        !!action.payload?.schema?.geometry?.spatialReference?.wkid;
      const isGeometryFormat = KNOW_LOCATION_FORMATS.includes(
        action.payload.formatName
      );
      state.hasDerivedGeometry = isGeometryFormat && hasWkid && hasGeometryType;
    },
    fieldToggled: (state, action: PayloadAction<string>) => {
      state.schemaFields[action.payload].mapped =
        !state.schemaFields[action.payload].mapped;
    },
    dataTypeChanged: (
      state,
      action: PayloadAction<{ dataType: DataType; fieldName: string }>
    ) => {
      state.schemaFields[action.payload.fieldName].dataType =
        action.payload.dataType;
    },
    fieldNameChanged: (
      state,
      action: PayloadAction<{ newName: string; fieldName: string }>
    ) => {
      state.schemaFields[action.payload.fieldName].toName =
        action.payload.newName;
    },
    schemaReset: resetSchema
  },
  extraReducers: builder => {
    builder.addCase(newCalculationStarted, resetSchema);
  }
});

export const {
  sampleChanged,
  formatChanged,
  formatPropChanged,
  schemaReceived,
  fieldToggled,
  dataTypeChanged,
  fieldNameChanged,
  schemaReset
} = counterSlice.actions;

export const inputSampleSelector = (state: RootState) =>
  state.schema.inputSample;

export const formatNameSelector = (state: RootState) =>
  state.schema.formatName || 'Unknown';

export const formatPropsSelector = (state: RootState) =>
  state.schema.formatProps;

export const schemaFieldsSelector = (state: RootState) =>
  state.schema.schemaFields;

export const derivedSamplesSelector = (state: RootState) =>
  state.schema.derivedSamples;

export const fieldsSelector = (state: RootState) =>
  Object.entries(state.schema.schemaFields)
    .filter(([, { mapped }]) => mapped)
    .map(([fieldName, { mapped, toName, dataType }]) => ({
      fieldName,
      mapped,
      toName,
      dataType
    }));

export const allFieldsSelector = createSelector(fieldsSelector, fields =>
  fields.map(({ fieldName, toName }) => ({
    fieldName,
    displayName: toName || fieldName
  }))
);

const numericFieldsSelector = createSelector(fieldsSelector, fields =>
  fields.filter(({ dataType }) =>
    ['Int32', 'Int64', 'Float32', 'Float64'].includes(dataType)
  )
);

export const numericFieldNamesSelector = createSelector(
  numericFieldsSelector,
  fields => fields.map(({ fieldName }) => fieldName)
);

export const xFieldOptionsSelector = createSelector(
  numericFieldsSelector,
  yFieldSelector,
  zFieldSelector,
  (fields, yField, zField) =>
    fields
      .filter(({ fieldName }) => fieldName !== yField && fieldName !== zField)
      .map(({ fieldName, toName }) => ({
        fieldName,
        displayName: toName || fieldName
      }))
);

export const yFieldOptionsSelector = createSelector(
  numericFieldsSelector,
  xFieldSelector,
  zFieldSelector,
  (fields, xField, zField) =>
    fields
      .filter(({ fieldName }) => fieldName !== xField && fieldName !== zField)
      .map(({ fieldName, toName }) => ({
        fieldName,
        displayName: toName || fieldName
      }))
);

export const zFieldOptionsSelector = createSelector(
  numericFieldsSelector,
  xFieldSelector,
  yFieldSelector,
  (fields, xField, yField) =>
    fields
      .filter(({ fieldName }) => fieldName !== xField && fieldName !== yField)
      .map(({ fieldName, toName }) => ({
        fieldName,
        displayName: toName || fieldName
      }))
);

export const stringFieldSelector = (state: RootState) =>
  Object.entries(state.schema.schemaFields)
    .filter(([, { mapped }]) => mapped)
    .filter(([, { dataType }]) => dataType === 'String')
    .map(([fieldName, { toName }]) => ({
      fieldName,
      displayName: toName || fieldName
    }));

export const hasDerivedGeometrySelector = (state: RootState) =>
  state.schema.hasDerivedGeometry;

export const getHumanReadableFormat = (formatName: FormatName) =>
  HUMAN_REDABLE_FORMAT[formatName];

export default counterSlice.reducer;
